/**
 * El enlace de una invitación de traspaso, que tiene que sobrevivir al login.
 *
 * Quien recibe un sitio casi nunca tiene cuenta todavía —por eso la invitación
 * se manda a un correo y no a un id—, así que el camino normal es: abre el
 * enlace, no hay sesión, se registra, y entonces sí. Pero la aplicación reescribe
 * cualquier ruta sin sesión a `/login` y ahí el token se perdía: quien acababa
 * de crear su cuenta aterrizaba en un panel vacío, sin ni una pista de que
 * venía a recibir un sitio.
 *
 * Se guarda en `sessionStorage` y no en `localStorage` a propósito. Es de ESTA
 * visita: dejarlo en el almacenamiento permanente significaría que meses
 * después, en otra pestaña, la aplicación intentaría abrir una invitación que
 * ya se aceptó o caducó.
 */
const KEY = 'veltica:transfer-invite'

/** El token si la dirección actual es la de una invitación. */
export function transferTokenFromPath(pathname = window.location.pathname): string | null {
  const match = /^\/transfer\/([A-Za-z0-9._-]{20,200})\/?$/.exec(pathname)
  return match?.[1] ?? null
}

/**
 * La dirección del traspaso que se abre DESDE el panel.
 *
 * El camino del correo trae un secreto en la ruta; éste trae sólo el id, porque
 * es lo único que el panel conoce —del token guardamos su huella, no él—. No
 * autoriza nada por su cuenta: el servidor sigue exigiendo que el correo de la
 * cuenta sea el de la invitación, así que el id de más en la barra no abre
 * ninguna puerta.
 *
 * Existe como dirección y no como estado dentro de `Home` para que el banner
 * pueda llevar aquí desde cualquier rincón del taller, que es donde de verdad
 * se está cuando llega un traspaso.
 */
export function transferIdFromSearch(search = window.location.search): string | null {
  const value = new URLSearchParams(search).get('transfer')
  return value && /^[A-Za-z0-9-]{10,64}$/.test(value) ? value : null
}

/** La misma dirección, para navegar hacia ella. */
export function transferReviewHref(transferId: string): string {
  return `/?transfer=${encodeURIComponent(transferId)}`
}

/**
 * El repaso que sigue a aceptar un traspaso, por el id del proyecto.
 *
 * Es una dirección y no un estado por lo mismo que la de arriba, y aquí además
 * es obligatorio: el paso de cobros SALE a Stripe y vuelve por una redirección
 * suya. Sin una dirección a la que volver, el asistente se perdía justo en el
 * único paso que no se puede hacer dentro de la aplicación.
 */
export function handoverIdFromSearch(search = window.location.search): string | null {
  const value = new URLSearchParams(search).get('handover')
  return value && /^[A-Za-z0-9-]{10,64}$/.test(value) ? value : null
}

export function handoverHref(projectId: string): string {
  return `/?handover=${encodeURIComponent(projectId)}`
}

export function rememberTransferToken(token: string): void {
  try { sessionStorage.setItem(KEY, token) } catch { /* modo privado */ }
}

export function readTransferToken(): string | null {
  try { return sessionStorage.getItem(KEY) } catch { return null }
}

/**
 * Lo lee y lo borra a la vez.
 *
 * Se consume al abrir la pantalla, no al aceptar: si se dejara puesto, alguien
 * que rechaza la invitación y sigue navegando volvería a caer en ella cada vez
 * que recargara.
 */
export function consumeTransferToken(): string | null {
  const token = readTransferToken()
  try { sessionStorage.removeItem(KEY) } catch { /* modo privado */ }
  return token
}
